import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const SOURCE = 'ajax-add-to-cart';

/** @type {ReturnType<typeof setTimeout> | undefined} */
let legacyBridgeTimeout;

/**
 * @returns {string[]}
 */
function getCartSectionIds() {
  /** @type {string[]} */
  const sectionIds = [];

  document.querySelectorAll('cart-items-component[data-section-id]').forEach((element) => {
    if (element instanceof HTMLElement && element.dataset.sectionId) {
      sectionIds.push(element.dataset.sectionId);
    }
  });

  return sectionIds;
}

/**
 * @returns {Promise<object>}
 */
async function fetchCart() {
  const response = await fetch('/cart.js');

  if (!response.ok) {
    throw new Error('Failed to fetch cart');
  }

  return response.json();
}

/**
 * @param {object} cart
 * @param {string} sourceId
 * @param {Record<string, unknown>} [data]
 */
function dispatchCartAdd(cart, sourceId, data = {}) {
  document.dispatchEvent(
    new CartAddEvent(cart, sourceId, {
      source: SOURCE,
      itemCount: cart?.item_count ?? 0,
      ...data,
    })
  );
}

/**
 * Bridge legacy AI block events to the theme cart drawer.
 */
function scheduleLegacyCartBridge() {
  if (legacyBridgeTimeout) {
    clearTimeout(legacyBridgeTimeout);
  }

  legacyBridgeTimeout = setTimeout(async () => {
    legacyBridgeTimeout = undefined;

    try {
      const cart = await fetchCart();
      dispatchCartAdd(cart, 'legacy-bridge', {
        itemCount: cart.item_count,
      });
    } catch (error) {
      console.error('Failed to refresh cart after legacy add-to-cart event', error);
    }
  }, 50);
}

/**
 * @param {HTMLFormElement} form
 * @returns {boolean}
 */
function shouldInterceptForm(form) {
  if (!form || form.dataset.ajaxCart === 'false') return false;
  if (form.closest('product-form-component, quick-order-list-component')) return false;

  const action = form.getAttribute('action') || '';

  if (action.includes('/cart/add')) return true;

  const hasVariantInput = Boolean(form.querySelector('[name="id"]'));
  const hasQuantityInput = Boolean(form.querySelector('[name="quantity"]'));
  const isCartUpdateForm = Boolean(form.querySelector('[name="updates[]"]'));

  return hasVariantInput && hasQuantityInput && !isCartUpdateForm;
}

/**
 * @param {HTMLFormElement} form
 * @param {SubmitEvent} event
 */
async function addToCartViaAjax(form, event) {
  event.preventDefault();

  const submitter = event.submitter instanceof HTMLButtonElement ? event.submitter : null;

  if (submitter) {
    submitter.disabled = true;
  }

  try {
    const formData = new FormData(form);
    const sectionIds = getCartSectionIds();

    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });
    const cartAddUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';

    const response = await fetch(cartAddUrl, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    });

    const result = await response.json();

    if (result.status) {
      const message = result.message || result.description || 'Failed to add to cart';
      console.error(message, result.errors);
      return;
    }

    const cart = await fetchCart();

    dispatchCartAdd(cart, form.id || SOURCE, {
      itemCount: cart.item_count,
      sections: result.sections,
      variantId: formData.get('id')?.toString(),
    });
  } catch (error) {
    console.error('Ajax add to cart failed', error);
  } finally {
    if (submitter) {
      submitter.disabled = false;
    }
  }
}

/**
 * @param {SubmitEvent} event
 */
function handleFormSubmit(event) {
  const form = event.target;

  if (!(form instanceof HTMLFormElement)) return;
  if (event.defaultPrevented) return;
  if (!shouldInterceptForm(form)) return;

  addToCartViaAjax(form, event);
}

function init() {
  document.addEventListener('submit', handleFormSubmit, true);

  ['cart:updated', 'cart:item-added'].forEach((eventName) => {
    document.addEventListener(eventName, scheduleLegacyCartBridge);
  });
}

init();
